import { InMemoryCache } from "@apollo/client/cache";
import { ApolloClient, ApolloLink, HttpLink, from, split, type Reference } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient as createWsClient } from "graphql-ws";
import { Observable, tap } from "rxjs";
import { graphqlHttpUrl, graphqlWsUrl } from "@/lib/config";
import { CORRELATION_HEADER, newCorrelationId } from "@/lib/correlation";
import { logDiagnostic } from "@/lib/diagnostics";
import { CODES } from "@/lib/diagnostics/contract";
import { csrfToken } from "@/auth/csrf";
import { markServerContact } from "@/lib/server-contact";
import { bootstrapAuth } from "@/auth/me";
import { redirectToLogin } from "@/auth/actions";
import { useUiStore } from "@/stores/ui-store";
import { wireCompanyContextCacheReset } from "./company-context-cache";

// Single ApolloClient instance for the whole app lifetime. Created
// once at module load; the auth-flip-recreation pattern from the
// legacy urql provider is explicitly NOT reproduced here - recreating
// the client wipes the normalized cache and triggers query storms.
//
// Auth lives in the BFF session. The browser never holds the access
// token; `credentials: 'include'` forwards the session cookie on
// every request. The BFF reads the cookie, injects the Bearer toward
// genesis, and synthesizes UNAUTHORIZED envelopes when there's no
// session. Apollo's errorLink converts those into navigation events.

const httpLink = new HttpLink({
  uri: graphqlHttpUrl,
  credentials: "include",
});

// Subscriptions ride a WebSocket to the BFF. The BFF holds the access
// token and injects it into the upstream genesis WS handshake - the
// browser never sees a bearer in connectionParams. graphql-ws is in
// lazy mode by default: the socket only opens when the first
// subscription is started, so /auth/login and other pre-auth render
// paths don't open a doomed connection.
const wsLink = new GraphQLWsLink(
  createWsClient({
    url: () => graphqlWsUrl(),
    // No connectionParams. The BFF rewrites the ConnectionInit
    // payload server-side to inject the bearer; sending anything
    // here would be ignored anyway.
    lazy: true,
    // 4401 from the BFF means the session has gone away. Stop
    // retrying and bounce the user to login on next paint. Any
    // other close reason (network blip, server restart) is retryable
    // - graphql-ws's built-in exponential backoff handles it.
    shouldRetry: (errOrCloseEvent) => {
      if (
        errOrCloseEvent &&
        typeof errOrCloseEvent === "object" &&
        "code" in errOrCloseEvent &&
        (errOrCloseEvent as { code: unknown }).code === 4401
      ) {
        if (typeof window !== "undefined") {
          redirectToLogin();
        }
        return false;
      }
      return true;
    },
    // WebSocket lifecycle diagnostics (ticket #126). The connection's
    // authoritative correlation ID is server-side (BFF assigns it at upgrade),
    // so these requestless events omit it. Normal closes are not reported.
    on: {
      error: () => {
        logDiagnostic({
          level: "error",
          code: CODES.websocket.CONNECTION_ERROR,
          category: "websocket",
          details: { phase: "error" },
        });
      },
      closed: (event) => {
        const code = (event as { code?: number } | undefined)?.code;
        // 1000/1001 are normal; 4401 is handled by shouldRetry (re-login).
        if (code === undefined || code === 1000 || code === 1001 || code === 4401) return;
        logDiagnostic({
          level: "warn",
          code: CODES.websocket.CLOSED,
          category: "websocket",
          details: { phase: "closed", closeCode: code },
        });
      },
    },
  }),
);

// Stamp a fresh request-scoped correlation ID on every Apollo operation
// (ticket #124). The BFF preserves a canonical v4 and echoes the authoritative
// value; genesis receives it too. We also stash it in the operation context so
// the errorLink can attach it to a GraphQL/HTTP diagnostic event (Phase 4)
// without pretending the SPA operation ID is the BFF's own request ID.
const correlationLink = new ApolloLink((operation, forward) => {
  const correlationId = newCorrelationId();
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, [CORRELATION_HEADER]: correlationId },
    correlationId,
  }));
  return forward(operation);
});

// Record that the BFF answered, for the session watcher's gate (ticket #67).
// `next` only: a GraphQL-error result still means the server responded, while a
// transport failure arrives on `error` and is not contact. Erring towards "no
// contact" only ever costs an extra liveness check, never a missed one.
export const serverContactLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(tap({ next: () => markServerContact() })),
);

// Attach CSRF token to every mutating operation. The BFF requires
// X-CSRF-Token on POSTs (Apollo always POSTs). The token comes from
// /auth/me - see apps/web/src/auth/csrf.ts.
const csrfLink = new ApolloLink((operation, forward) => {
  const token = csrfToken();
  if (token) {
    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
      headers: { ...headers, "x-csrf-token": token },
    }));
  }
  return forward(operation);
});

// Stamp the app locale on Accept-Language so genesis localizes reference data
// (localizedName + single-`name` Country/CountryState) to the app language, not
// the browser's. Read at request time so it tracks the current locale.
export const localeLink = new ApolloLink((operation, forward) => {
  const locale = useUiStore.getState().locale;
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, "accept-language": locale },
  }));
  return forward(operation);
});

// The BFF's CSRF plugin rejects POSTs with a stale `x-csrf-token` header with
// HTTP 403 + code `FST_CSRF_INVALID_TOKEN`. Apollo wraps this as a ServerError-
// shaped object (statusCode + parsed `result` body). The in-memory token can go
// stale across BFF restarts (session keys live, but the token issued by the
// previous process is no longer valid). Without recovery the user sees the
// generic "Something went wrong" toast — for a transient state we own.
export function isCsrfRejection(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    statusCode?: number;
    response?: { status?: number };
    result?: { code?: string; message?: string };
    bodyText?: string;
  };
  const status = e.statusCode ?? e.response?.status;
  if (status !== 403) return false;
  if (e.result?.code === "FST_CSRF_INVALID_TOKEN") return true;
  if (e.result?.message === "Invalid csrf token") return true;
  return typeof e.bodyText === "string" && e.bodyText.includes("FST_CSRF_INVALID_TOKEN");
}

// Guards against an infinite loop if the re-fetch produces a token the BFF
// also rejects (config-broken state). One retry is enough for the legitimate
// "BFF restarted, my token is stale" case.
const CSRF_RETRY_FLAG = "__csrfRetried";

// Apollo v4 collapsed onError's split graphQLErrors/networkError args into a
// single `error` field. CombinedGraphQLErrors.is() distinguishes a GraphQL
// errors envelope (which carries `.errors[]`) from a plain network error.
//
// `refreshAuth` is injected so the link is testable without standing up the
// browser /auth/me fetch. In production we bind to bootstrapAuth.
// The Apollo error link is the SOLE transport logger for Apollo operations
// (ticket #126): components must not also log the same failed operation. The
// per-operation correlation ID (set by correlationLink) rides in context so a
// diagnostic can be tied to the BFF request that handled the operation.
function operationTypeOf(operation: {
  query: Parameters<typeof getMainDefinition>[0];
}): "query" | "mutation" | "subscription" | undefined {
  const def = getMainDefinition(operation.query);
  return def.kind === "OperationDefinition" ? def.operation : undefined;
}

function networkStatus(error: unknown): number | undefined {
  if (error && typeof error === "object") {
    const e = error as { statusCode?: number; response?: { status?: number } };
    const status = e.statusCode ?? e.response?.status;
    if (typeof status === "number") return status;
  }
  return undefined;
}

// Distinct non-empty string values of one extension key across a GraphQL error
// list, joined with ",". Returns undefined when none carry it — keeps the
// diagnostic detail off the wire rather than emitting an empty string.
function distinctExtension(
  errors: ReadonlyArray<{ extensions?: Record<string, unknown> }>,
  key: "code" | "classification",
): string | undefined {
  const seen = new Set<string>();
  for (const err of errors) {
    const value = err.extensions?.[key];
    if (typeof value === "string" && value.length > 0) seen.add(value);
  }
  return seen.size > 0 ? [...seen].join(",") : undefined;
}

export function createErrorLink(refreshAuth: () => Promise<unknown>): ApolloLink {
  return onError(({ error, operation, forward }) => {
    if (isCsrfRejection(error) && !operation.getContext()[CSRF_RETRY_FLAG]) {
      return new Observable((observer) => {
        refreshAuth()
          .then(() => {
            operation.setContext({ [CSRF_RETRY_FLAG]: true });
            forward(operation).subscribe({
              next: (result) => observer.next(result),
              error: (err) => observer.error(err),
              complete: () => observer.complete(),
            });
          })
          .catch((bootstrapErr) => observer.error(bootstrapErr));
      });
    }

    const correlationId = operation.getContext().correlationId as string | undefined;
    const operationName = operation.operationName || undefined;
    const operationType = operationTypeOf(operation);

    if (CombinedGraphQLErrors.is(error)) {
      if (error.errors.some((err) => err.extensions?.code === "UNAUTHORIZED")) {
        // BFF returned the synthesized envelope. Normal auth flow, not a
        // diagnostic. Trigger a full re-login as a fallback for direct-query
        // callers that bypass the requireAuth loader.
        if (typeof window !== "undefined") redirectToLogin();
        return;
      }
      // Capture the genesis error signature (code + classification, still never
      // the raw messages/paths) so the exact failure the incident was
      // reconstructed by hand — incl. the code-less NullValueInNonNullableField,
      // which carries only a classification — is queryable in telemetry (#228 C2).
      const codes = distinctExtension(error.errors, "code");
      const classifications = distinctExtension(error.errors, "classification");
      // One diagnostic per failed operation (never the raw messages/paths).
      logDiagnostic({
        level: "error",
        code: CODES.graphql.OPERATION_ERROR,
        category: "graphql",
        correlationId,
        details: {
          kind: "graphql",
          ...(operationName ? { operationName } : {}),
          ...(operationType ? { operationType } : {}),
          ...(codes ? { code: codes } : {}),
          ...(classifications ? { classification: classifications } : {}),
        },
      });
      if (import.meta.env.DEV) {
        console.error(`[graphql] ${operation.operationName}: operation error`);
      }
      return;
    }

    logDiagnostic({
      level: "error",
      code: CODES.graphql.TRANSPORT_FAILURE,
      category: "graphql",
      correlationId,
      details: {
        kind: "network",
        ...(operationName ? { operationName } : {}),
        ...(operationType ? { operationType } : {}),
        ...(networkStatus(error) !== undefined ? { status: networkStatus(error) } : {}),
      },
    });
    if (import.meta.env.DEV) console.error(`[graphql:network] ${operation.operationName}`);
  });
}

const errorLink = createErrorLink(bootstrapAuth);

// Route subscriptions to the WS link, everything else to HTTP.
const splitLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === "OperationDefinition" && def.operation === "subscription";
  },
  wsLink,
  httpLink,
);

// Relay-ish connection (`{ nodes, totalCount, pageInfo }`) for screens that
// page through to completion. `keyArgs` drops the pagination cursor so every
// page of one logical list (same filter/order) lands in a SINGLE cache entry;
// `merge` appends each `after` page onto the running list, de-duped. A
// cursor-less fetch (initial load or post-mutation refetch) replaces, so the
// accumulated list can never go stale. Callers that never send `after`
// (e.g. the share-flow document picker) keep their plain replace behaviour.
//
// `identify` says how to recognise the same row twice. The default is the
// normalized cache ref, which is right only for node types Apollo normalizes -
// i.e. those with an `id`. A type WITHOUT one is stored inline and has no
// `__ref` at all, so a set built from it would hold a single `undefined` and
// reject every incoming row: page two would silently vanish. Hence the rule
// below is "drop only what is positively recognised as a duplicate" rather
// than "keep only what is positively new", and a caller whose nodes are not
// normalized passes an identity that suits them.
type CursorNode = Partial<Reference> & { [field: string]: unknown };
type CursorConnection = { nodes?: ReadonlyArray<CursorNode>; [field: string]: unknown };

const filterAndOrder = (input: Record<string, unknown>): unknown => ({
  where: input["where"] ?? null,
  order: input["order"] ?? null,
});

function cursorConnectionField(
  keyOf: (input: Record<string, unknown>) => unknown = filterAndOrder,
  identify: (node: CursorNode) => string | undefined = (node) => node.__ref,
) {
  return {
    keyArgs: (args: Record<string, unknown> | null): string =>
      JSON.stringify(keyOf((args?.input ?? {}) as Record<string, unknown>)),
    merge(
      existing: CursorConnection | undefined,
      incoming: CursorConnection,
      options: { args: Record<string, unknown> | null },
    ): CursorConnection {
      const after = (options.args?.input as { after?: string } | undefined)?.after;
      if (!after || !existing?.nodes) return incoming;
      const seen = new Set(
        existing.nodes.map(identify).filter((id): id is string => id != null),
      );
      return {
        ...incoming,
        nodes: [
          ...existing.nodes,
          ...(incoming.nodes ?? []).filter((node) => {
            const id = identify(node);
            return id == null || !seen.has(id);
          }),
        ],
      };
    },
  };
}

/**
 * The app's normalized cache, as a factory so a test can drive the REAL field policies rather than
 * a copy of them that could drift from these.
 */
export function createAppCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // Defaults already handle Person.id, Document.id, etc. since
      // genesis exposes `id` as a String!. Add custom keyFields for
      // entities without `id` when queries land.
      //
      // Questionnaire groups are read through two selections of the same root
      // field: the catalogue without `parts` (which costs a per-user answered
      // probe), the group pages with them. Without a key the two would collide
      // as one unnormalized list and evict each other's fields on every write.
      QuestionnaireGroup: { keyFields: ["questionnaireGroupId"] },
      Query: {
        fields: {
          documents: cursorConnectionField(),
          curriculumVitaes: cursorConnectionField(),
          // The company member strip (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not). Keyed by `companyId`
          // and nothing else: two companies' rosters must never accumulate
          // into one entry, and `first`/`after` are the pagination the merge
          // exists to fold together. `CompanyMembership` carries no `id`, so
          // it is stored inline and has no cache ref - the row's identity
          // within one company is its person, which is also genesis's primary
          // key for it minus the company this entry is already keyed by.
          companyMembers: cursorConnectionField(
            (input) => ({ companyId: input["companyId"] ?? null }),
            (node) => (node["person"] as Reference | undefined)?.__ref,
          ),
        },
      },
    },
  });
}

export const apolloClient = new ApolloClient({
  link: from([errorLink, serverContactLink, correlationLink, csrfLink, localeLink, splitLink]),
  cache: createAppCache(),
  // Stale-while-revalidate as the app-wide default: pages render instantly
  // from the normalized cache, then every fresh mount revalidates against
  // genesis in the background and patches the UI - so data written by
  // mutations elsewhere (e.g. the Hardskill-Analyse writing EXPERTISE rows)
  // shows up on navigation without a hard refresh. `nextFetchPolicy` drops
  // the observable back to cache-first after the revalidate, so re-renders
  // of a mounted page don't spam the BFF; the next navigation (new mount)
  // revalidates again.
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  },
  // Apollo v4 moved the devtools toggle into a `devtools` config object.
  devtools: { enabled: import.meta.env.DEV },
});

// On a locale flip, relabel on-screen data: cached values keep the old language
// (new mounts self-heal via cache-and-network, but the current view won't).
useUiStore.subscribe((state, prev) => {
  if (state.locale === prev.locale) return;
  void apolloClient.refetchQueries({ include: "active" }).catch(() => {});
});

// On a company context switch, EMPTY the cache and re-read — not the refetch above it. A locale
// flip relabels the same entities; a context switch changes which reader they were fetched for, and
// one normalized cache keyed by entity id would otherwise serve company A's entities to a reader
// acting as company B. The policy and its reasoning live in ./company-context-cache.
wireCompanyContextCacheReset(apolloClient);
